/**
 * @fileoverview Organism: a GitHub/GitLab-style contribution graph.
 * @module components/organisms/ActivityHeatmap
 *
 * @description
 * One cell per day, reps summed across every habit. `buildHeatmap` always
 * starts its window on a Sunday and ends it on a Saturday, so laying the
 * flat, chronological cell list into a `grid-auto-flow: column` grid with
 * seven rows naturally reproduces the familiar week columns.
 */

import { buildHeatmap } from "@/lib/domain/stats"
import type { HabitEntry } from "@/types/types"

import "./ActivityHeatmap.css"

/** Props accepted by {@link ActivityHeatmap}. */
export interface ActivityHeatmapProps {
	/** Every logged entry, across every habit. */
	readonly entries: readonly HabitEntry[]
	/** Trailing window size, in weeks. Defaults to 53 (about a year). */
	readonly weeks?: number
}

const LEVEL_LABELS = ["Aucune", "Faible", "Modérée", "Élevée", "Très élevée"]

/**
 * French-formatted long date for a `YYYY-MM-DD` calendar key.
 *
 * @param dateKey - The calendar date to format.
 * @returns A long date, e.g. `"16 septembre 2026"`.
 */
function formatLongDate(dateKey: string): string {
	const [year, month, day] = dateKey.split("-").map(Number)
	const date = new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1)
	return new Intl.DateTimeFormat("fr-FR", {
		day: "numeric",
		month: "long",
		year: "numeric",
	}).format(date)
}

/**
 * Activity heatmap: repetitions logged per day, over a trailing window.
 *
 * @param props - The entries to plot and the window size.
 * @returns The rendered graph.
 */
export function ActivityHeatmap({ entries, weeks = 53 }: ActivityHeatmapProps) {
	const cells = buildHeatmap(entries, { weeks })
	const totalReps = cells.reduce((sum, cell) => sum + cell.count, 0)

	return (
		<div className="heatmap">
			<div
				className="heatmap__grid"
				style={{ gridTemplateColumns: `repeat(${String(weeks)}, 1fr)` }}
				role="img"
				aria-label={`${String(totalReps)} répétitions au total sur les ${String(weeks)} dernières semaines`}
			>
				{cells.map(cell => (
					<span
						key={cell.date}
						className={`heatmap__cell heatmap__cell--${String(cell.level)}`}
						title={`${String(cell.count)} répétition${cell.count === 1 ? "" : "s"} — ${formatLongDate(cell.date)}`}
						aria-hidden="true"
					/>
				))}
			</div>

			<div className="heatmap__legend" aria-hidden="true">
				<span>Moins</span>
				{[0, 1, 2, 3, 4].map(level => (
					<span
						key={level}
						className={`heatmap__cell heatmap__cell--${String(level)}`}
						title={LEVEL_LABELS[level]}
					/>
				))}
				<span>Plus</span>
			</div>
		</div>
	)
}
